import { filterConfigs, formatRichText } from "../../functions";
import { NonStaticComponents } from "../../components/NonStaticComponents";
import { HEADER_IMAGE_VARIANT, CONFIGURATION_TYPE_IDS, HEADER_TEXT_VARIANT } from "../../constants";
import {
	SemiHeartMask,
	ImageWithDescription,
	MobileParallax,
	HomePageParallax,
	DefaultHeaderImage,
} from "../../components/HeaderImages";
import { RelevantServices } from "./RelevantServices";
import { parseClassesIntoHtml } from "../../functions/styling";
import { HEADER_STYLING_CLASS_MAP } from "@iscr/contentful/components/CTA";
import { Button } from "semantic-ui-react";
import SIcon from "@iscr/views/NUI/StreamlineIcons/SIcon";
import Alert from "@iscr/views/NUI/Alert";

const PAGE_HEADER_IMAGE_COMPONENT_MAP = {
	[HEADER_IMAGE_VARIANT.DEFAULT]: DefaultHeaderImage,
	[HEADER_IMAGE_VARIANT.SEMI_HEART_MASK]: SemiHeartMask,
	[HEADER_IMAGE_VARIANT.MOBILE_PARALLAX]: MobileParallax,
	[HEADER_IMAGE_VARIANT.HOME_PAGE_PARALLAX]: HomePageParallax,
	[HEADER_IMAGE_VARIANT.IMAGE_WITH_DESCRIPTION]: ImageWithDescription,
};

const FIXED_HEIGHT = {
	mobile: "h-[500px]",
	desktop: "lg:h-[550px]",
};

export const PageHeaderSection = ({
	ctas,
	subtitle,
	headerText,
	description,
	images,
	nonStaticComponentIds,
	isStatic,
	headerImageDisplayType,
	headerTextDisplayType,
	displayRelevantServicesMenu = false,
	displayDescriptionAboveNonStaticComponents = false,
	formattedSectionStyles = {},
	siteStatus,
	configurations = [],
	parsedFunctions,
	extraSubtitle,
	exp
}) => {
	const HeaderImageComponent = PAGE_HEADER_IMAGE_COMPONENT_MAP[headerImageDisplayType] || DefaultHeaderImage;
	const headerDisplayOrder =
		headerImageDisplayType === HEADER_IMAGE_VARIANT.SEMI_HEART_MASK ? "flex-col" : "flex-col-reverse";
	const sectionStyling = headerImageDisplayType === HEADER_IMAGE_VARIANT.HOME_PAGE_PARALLAX && HEADER_STYLING_CLASS_MAP["Gradient"] || "";
	const textCentering = formattedSectionStyles.descriptionStyling
	const headerConfiguration = filterConfigs(configurations, CONFIGURATION_TYPE_IDS.RICH_TEXT)?.[0];
	const medcert = headerTextDisplayType === HEADER_TEXT_VARIANT.MEDCERT;

	const descriptionEl = 
				<PageHeaderDescription
					description={description}
					siteStatus={siteStatus}
					configurations={configurations}
					parsedFunctions={parsedFunctions}
					descriptionStyling={formattedSectionStyles.descriptionStyling}
					isStatic={isStatic}
					descriptionConfiguration={headerConfiguration}
					headerTextDisplayType={headerTextDisplayType}
				/>

	const nonStaticEl = (
		<PageHeaderNonStaticComponent
			nonStaticComponentIds={nonStaticComponentIds}
			isStatic={isStatic}
			siteStatus={siteStatus}
			exp={exp}
		/>
	);

	return (
		<section
			className={`header bg-is2-blue text-white relative px-0 lg:rounded-br-[100px] ${sectionStyling}`}
			data-testid="area-header"
		>
			<div
				className={`flex ${headerDisplayOrder} lg:flex-row flex-wrap w-[100%] p-0 lg:min-h-[660px] max-w-[1124px] m-auto ${headerImageDisplayType === HEADER_IMAGE_VARIANT.MOBILE_PARALLAX ? "items-end" : "items-center"} ${headerConfiguration?.heartOverlay?"relative" : ""}`}
			>
				<HeaderImageComponent headerImages={images} imgStyling={headerConfiguration?.headerDesktopStyling||""} />
				{headerConfiguration?.heartOverlay && <HeartImageOverlay />}
				{medcert && <Button secondary className="absolute bottom-[64px] translate-x-1/2 lg:translate-x-0 right-1/2 lg:right-[calc(50%_+_140px)] !flex justify-center items-center gap-2 blue !px-8 w-[280px] !shadow-[0_0_12px_0_rgba(0,0,0,0.5)]" onClick={() => app.trigger(app.events.SHOW_POPUP, {pt:"cx_txt", pn:'med-cert-preview', size:"fullscreen", noCloseBtn:true, noHeader:true })}>View example certificate <SIcon bold name="external" size="xs"/></Button>}

				<div className={`${headerConfiguration?.headerMobileOverlay && "mt-0 lg:mt-[110px] lg:mb-[104px]" ||`md:my-8 mx-auto pt-11 ${medcert ? "pb-4 md:pb-8" : "pb-10"} max-w-[700px]`} lg:w-1/2 ${exp?.waitTimeExp && "xl:w-auto"}`}>
					<PageHeaderText 
						textCentering={textCentering}
						subtitle={!medcert && subtitle}
						extraSubtitle={(!medcert && !exp?.waitTimeExp) && extraSubtitle}
						headerTextDisplayType={headerTextDisplayType}
						headerConfiguration={headerConfiguration}
						headerText={headerText}
					/>

					<AlternativeMobileImage headerImageDisplayType={headerImageDisplayType} images={images} imgStyling={headerConfiguration?.headerMobileStyling||""} textOverlay={headerConfiguration?.headerMobileOverlay}/>
					
					{medcert && <Alert className="h-8 items-center !w-fit mx-auto md:mx-0 !mb-8" bg="bg-is2-green-150" wait={true} hideIcon content={<div className="flex items-center">Average wait time: <SIcon name="clock" size="xs" cls="text-black" bold/> <span className="font-medium">under 5 minutes*</span></div>}/>}
					
					{displayDescriptionAboveNonStaticComponents
						? <div className={`flex flex-col ${exp?.waitTimeExp ? "gap-[44px]":"gap-10"}`}>{descriptionEl}{nonStaticEl}</div>
						: <div className={`flex flex-col ${medcert ? 'gap-8 mt-0' : 'gap-6'}`}>{nonStaticEl}{descriptionEl}</div>}

					{ctas && siteStatus && (
						<div className={`${exp?.waitTimeExp && "max-w-[500px] mx-auto lg:mx-0"}`}>
							<RelevantServices
								ctas={ctas}
								siteStatus={siteStatus}
								isStatic={isStatic}
								displayRelevantServicesMenu={displayRelevantServicesMenu}
								parsedFunctions={parsedFunctions}
							/>
						</div>
					)}
					{(medcert || exp?.waitTimeExp) && <p className={`text-is2-blue-100 text-sm mt-4 ${textCentering}`}>{extraSubtitle}</p>}
				</div>
			</div>
		</section>
	);
};

/*
	For certain header image types - semi heart mask - the mobile image is beneath the header text
*/
const AlternativeMobileImage = ({ headerImageDisplayType, images, imgStyling, textOverlay }) => {
	if (headerImageDisplayType === HEADER_IMAGE_VARIANT.SEMI_HEART_MASK) {
		const imgIndex = images.length-1;
		return <img className={`${imgStyling||""} ${textOverlay ? "mb-0": "mb-8 lg:mb-0"} block lg:hidden w-full`} src={images[imgIndex]?.imageUrl} alt={images[imgIndex]?.alt} />;
	}
	return null;
};

const HeartImageOverlay = () => {
	return <img
			className="h-auto rounded-lg absolute left-[70vw] lg:left-[22vw] xl:left-[220px] top-[280px] lg:top-[320px] w-[80px] sm:w-[140px]"
			src="https://storage.googleapis.com/instant-med-public/v2/Header_heart_single.svg"
			alt="heart"
		/>
};

// Displays custom components - that can't be statically rendered - such as current wait time indicators ect

const PageHeaderNonStaticComponent = ({ nonStaticComponentIds, isStatic, siteStatus, exp }) => {
	if (!nonStaticComponentIds || nonStaticComponentIds.length === 0) {
		return null;
	}
	return (
		<div className={`${exp?.waitTimeExp ? "max-w-fit px-4 lg:pl-0 mb-2 w-full": "max-w-[400px]"} mx-auto lg:ml-0`}>
			<NonStaticComponents componentId={nonStaticComponentIds[0]} isStatic={isStatic} props={{ badge: true, siteStatus }} />
		</div>
	);
};

const PageHeaderText = ({ headerText, subtitle, extraSubtitle, headerTextDisplayType, textCentering, headerConfiguration }) => {
	const textOverlay = headerConfiguration?.headerMobileOverlay;

	return (
		<div className={`${textOverlay ? "flex flex-col justify-start absolute !mt-9 lg:!mt-0 lg:static" : ""}`}>
				{subtitle && (
				<div className={`${headerConfiguration?.subtitleStyling || "mb-4"}`}>
					<p className={`text-is2-blue-50 ${headerConfiguration?.subtitleTextCentering || textCentering} `}>
						<span className={`${headerTextDisplayType === HEADER_TEXT_VARIANT.HIGHLIGHTED ? "inline bg-white text-center px-2 pt-1 lg:pt-0 text-black" : ""} ${textOverlay ? "!text-is2-yellow-450 text-is2_lg lg:bg-transparent lg:!text-is2-blue-100 lg:px-0" : ""}`}>{subtitle}</span>
					</p>
				</div>
			)}
		<h1 className={`${textCentering} ${textOverlay ? "order-first !text-is2-blue-850 lg:!text-white !font-bold lg:!font-medium !text-left mb-0" : "!text-white !mb-8"} font-medium mt-0 mx-4 md:mx-0`}><span className={`${textOverlay && "bg-white lg:bg-transparent px-2 lg:mt-0 lg:p-0"}`}>{headerText}</span></h1>
		{extraSubtitle && <p className={`${headerConfiguration?.extraSubtitleStyling || "text-is2_lg mt-4 lg:mt-0 bg-is2-blue lg:bg-white lg:text-is2-yellow-700 w-fit px-2 !font-medium"}`}>{extraSubtitle}</p>}
	</div>
	);
};

const PageHeaderDescription = ({ description, siteStatus, descriptionStyling, descriptionConfiguration, configurations, parsedFunctions, isStatic = false, headerTextDisplayType }) => {
	const descriptionContent = formatRichText(description, siteStatus, configurations, parsedFunctions, isStatic);
	const wrapperStyling = descriptionConfiguration?.wrapperStyling;

	const DESCRIPTION_CLASSES = {
		p: `text-base ${descriptionConfiguration?.textStyling || descriptionStyling}`,
		div: `${descriptionConfiguration?.divStyling||""}`
	};

	return (
		<>
			{description && (
				<div className={`md:block mx-4 lg:mx-0 ${wrapperStyling || "text-left md:text-center lg:text-left"} ${descriptionConfiguration?.headerMobileOverlay && "[&>p:last-child]:lg:!hidden [&>p:first-child]:text-white"||""} ${headerTextDisplayType === HEADER_TEXT_VARIANT.MEDCERT && '[&_p:first-of-type]:text-is2-blue-100 [&_p:first-of-type]:!mb-8'}`}>
					{parseClassesIntoHtml(DESCRIPTION_CLASSES, descriptionContent)}
				</div>
			)}
		</>
	);
};
